import AvatarCustom from "../AvatarCustom/AvatarCustom";
import { IPetBillingPage, IWellnessPlanCardDetails }
  from "../../services/wellness-plans-by-user/wellness-plan-by-user.interface";
import { PAGES_PATH } from "../../navigation/params";
import React, { useEffect, useRef } from "react";
import { fromISOToFormatDate, fromUnixToFormattedVisitdateStr } from "../../utils/unix-timestamp";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../hooks/redux-hooks/user-store/user.store";
import { userActivityLogger } from "../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import { SUBSCRIPTION_CARD_EXPIRY_STATUS, MODALS, OVERDUE_STATUSES, OverdueStatus, EXPIRY_STATUS, WELLNESS_PLAN_TYPES }
  from "../../config/params";
import { getPlanOverdueText } from "../WellnessMemberPlanCard/WellnessMemberPlanCardOld";
import { PetInvoiceList } from "..";
import { useModal } from "../../context/ModalContext";
import { ChipCustom } from "../../styles/styledComponents";
import { useOverdueInvoiceStore } from "../../hooks/redux-hooks/overdue-invoice-store/overdue-invoice.store";
import { billCycle } from "../../shared/containers/WellnessPlanPaymentContainer/types";
import { useCombinedAlertsDataStore, useWellnessRenewalFunnel } from "../../hooks";
import { WELLNESS_RENEWAL_ENTRY_POINTS } from "../../config/params";
import { TotalCareRenewalBanner } from "../RenewalBanners";
import { StyledBookingFlowButton } from
  "../../shared/containers/BookingScheduleContainer/BookingScheduleContainer.style";
import { SCRATCHPAY_ACCOUNT_URL } from "../../config/params";

interface IPetPlanCard {
  activePetDetails: IPetBillingPage;
  subscriptionFee: string;
  nextBillingDate: string;
  planRenewalDate: string;
  planEndDate: string;
  planOverdueStatus: OverdueStatus | null;
  isPausedRenewUnsuccessful?:boolean;
  isCancelledRenewUnsuccessful?:boolean;
  isPlanEndingInFiveDays?: boolean;
  cardDetails?: IWellnessPlanCardDetails;
  hasWellnessPlan: boolean;
  billCycle: billCycle;
  planName: string;
  hasUpcomingRenewal?: boolean;
  renewalStartDate?: string;
  isScratchpay?: boolean;
}

export const getPlanType = (planType: string)=>{
  if (!planType) return "GoodPet+ Plan";

  switch (planType.toLowerCase()) {
  case WELLNESS_PLAN_TYPES.BASE:
    return "TotalCare Base Plan";
  case WELLNESS_PLAN_TYPES.PREMIUM:
    return "TotalCare Plan";
  default:
    return "GoodPet+ Plan";
  }
};

const PetPlanCard = ({
  activePetDetails,
  subscriptionFee,
  nextBillingDate,
  planRenewalDate,
  planEndDate,
  planOverdueStatus,
  cardDetails,
  isPausedRenewUnsuccessful=false,
  isCancelledRenewUnsuccessful=false,
  isPlanEndingInFiveDays=false,
  hasWellnessPlan,
  billCycle,
  planName,
  renewalStartDate,
  hasUpcomingRenewal = false,
  isScratchpay = false,
}: IPetPlanCard) => {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const { openModal } = useModal();
  const { cancelledWellnessPetList } = useOverdueInvoiceStore();
  const { endsSoonWPData } = useCombinedAlertsDataStore();
  const { startRenewalFunnel } = useWellnessRenewalFunnel();

  const isPetPlanCancelled = cancelledWellnessPetList.filter((petDetails: any) =>
    activePetDetails.id === petDetails.petId).length !== 0;
  const planEndingSoonData = endsSoonWPData.find((p) => p.petId === activePetDetails.id);
  const isScratchPayPaymentOverdue =
    isScratchpay && planOverdueStatus === OVERDUE_STATUSES.OVERDUE;
  const lastLoggedScratchpayDelinquencyKeyRef = useRef<string | null>(null);
  const overdueBannerClassName = (() => {
    if (isScratchPayPaymentOverdue) return "text-red-4 bg-red-3";
    if (
      planOverdueStatus === OVERDUE_STATUSES.OVERDUE ||
      planOverdueStatus === OVERDUE_STATUSES.PAUSED ||
      planOverdueStatus === OVERDUE_STATUSES.CANCELLED ||
      isPausedRenewUnsuccessful ||
      isCancelledRenewUnsuccessful
    ) {
      return "text-white bg-red-4";
    }
    return "text-red-4 bg-red-3";
  })();

  const handleManagePlanCtaClick = (petId: string, petName: string) => {
    if (!hasWellnessPlan) return;

    userActivityLogger(MIXPANEL_EVENTS.USER_CLICKS_MANAGE_PLAN,
      { pet_id: petId,
        pet_name: petName,
        referred_from: "plan_and_billing",
      });
    navigate(`${PAGES_PATH.MANAGE_PLAN}?id=${petId}`);
  };

  const handleContactCtaClick = () => {
    userActivityLogger(MIXPANEL_EVENTS.CANCELED_WP_USERS_CONTACT_HOSPITAL,
      {
        pet_name: activePetDetails.name,
        referred_from_page: "plan_and_billing",
      });
    openModal(MODALS.OVERDUE_CONTACT_MODAL);
  };

  const handleScratchPayNowClick = () => {
    if (
      isScratchpay &&
      (planOverdueStatus === OVERDUE_STATUSES.OVERDUE ||
        planOverdueStatus === OVERDUE_STATUSES.PAUSED)
    ) {
      userActivityLogger(MIXPANEL_EVENTS.DELINQUENCY_CTA_CLICKED, {
        provider: "scratchpay",
        delinquency_state: planOverdueStatus === OVERDUE_STATUSES.OVERDUE ? "overdue_notice" : "paused",
        entry_point: "plan_and_billing",
        surface: "banner",
        cta_type: "pay_now",
        pet_id: activePetDetails.id,
        pet_name: activePetDetails.name,
        plan_name: planName,
        is_scratchpay: true,
        payment_processor: "scratchpay",
        card_type: planOverdueStatus === OVERDUE_STATUSES.OVERDUE ? "overdue" : "benefits_paused",
        cta_action: "pay_now",
        has_payment_url: true,
        payment_url_source: "scratchpay_account_login",
        referred_from_page: "plan_and_billing",
      });
    }
    window.open(SCRATCHPAY_ACCOUNT_URL, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (!isScratchpay) return;
    if (planOverdueStatus !== OVERDUE_STATUSES.OVERDUE && planOverdueStatus !== OVERDUE_STATUSES.PAUSED) return;

    const key = `${activePetDetails.id}:${planOverdueStatus}`;
    if (lastLoggedScratchpayDelinquencyKeyRef.current === key) return;
    lastLoggedScratchpayDelinquencyKeyRef.current = key;

    userActivityLogger(MIXPANEL_EVENTS.DELINQUENCY_SURFACE_SHOWN, {
      provider: "scratchpay",
      delinquency_state: planOverdueStatus === OVERDUE_STATUSES.OVERDUE ? "overdue_notice" : "paused",
      entry_point: "plan_and_billing",
      surface: "banner",
      pet_id: activePetDetails.id,
      pet_name: activePetDetails.name,
      plan_name: planName,
      is_scratchpay: true,
      payment_processor: "scratchpay",
      card_type: planOverdueStatus === OVERDUE_STATUSES.OVERDUE ? "overdue" : "benefits_paused",
      has_payment_url: true,
      payment_url_source: "scratchpay_account_login",
      referred_from_page: "plan_and_billing",
    });
  }, [
    activePetDetails.id,
    activePetDetails.name,
    isScratchpay,
    planName,
    planOverdueStatus,
  ]);

  const renderCardStatusTag = () => {
    if (!cardDetails) {
      return null;
    }
    if (cardDetails.isActive === false ||
      cardDetails.expiryStatus?.toLowerCase() === EXPIRY_STATUS.DISABLED) {
      return <ChipCustom
        label={SUBSCRIPTION_CARD_EXPIRY_STATUS.DISABLED}
        className="!font-semibold !text-xs !py-0.5 absolute right-3"
        color={"default"}
        size="small"
      />;
    }
    const isCardExpiringSoon = cardDetails.expiryStatus?.toLowerCase() === EXPIRY_STATUS.EXPIRING_SOON;

    if (isCardExpiringSoon) {
      return <ChipCustom
        label={SUBSCRIPTION_CARD_EXPIRY_STATUS.EXPIRING_SOON}
        className="!font-semibold !text-xs !py-0.5 absolute right-3"
        color={"warning"}
        size="small"
      />;
    }
    const isCardExpired = cardDetails.expiryStatus?.toLowerCase() === EXPIRY_STATUS.EXPIRED;

    if (isCardExpired) {
      return <ChipCustom
        label={SUBSCRIPTION_CARD_EXPIRY_STATUS.EXPIRED}
        className="!font-semibold !text-xs !py-0.5 absolute right-3"
        color={"error"}
        size="small"
      />;
    }

    // eslint-disable-next-line max-len
    const isCardIssuerReportAlert = cardDetails.expiryStatus?.toLowerCase() === EXPIRY_STATUS.ISSUER_REPORT;

    if (isCardIssuerReportAlert) {
      return <ChipCustom
        label={SUBSCRIPTION_CARD_EXPIRY_STATUS.ISSUER_REPORT}
        className="!font-semibold !text-xs !py-0.5 absolute right-3"
        color={"warning"}
        size="small"
      />;
    }

    return null;
  };

  const formatDateString = (date: string, timeZone?: string) => {
    if (!date) {
      return "N/A";
    }

    return fromUnixToFormattedVisitdateStr(+date, timeZone);
  };

  const renderPlanStatusPrimaryLine = () => {
    if (planOverdueStatus === OVERDUE_STATUSES.PAUSED) {
      if (isPausedRenewUnsuccessful) {
        return (
          <div className="text-xs font-semibold">
            {`Plan Ended: ${formatDateString(planEndDate)}`}
          </div>
        );
      }

      return (
        <div className="text-xs font-semibold">
          {isScratchpay ? "Plan Paused for Non-Payment" : "Plan Temporarily Suspended"}
        </div>
      );
    }

    if (planOverdueStatus === OVERDUE_STATUSES.OVERDUE) {
      // Figma doesn't show an extra status row here (banner covers it).
      return null;
    }

    if (planOverdueStatus === OVERDUE_STATUSES.CANCELLED) {
      if (isCancelledRenewUnsuccessful) {
        return (
          <div className="text-xs font-semibold">
            {`Plan Ended: ${formatDateString(planEndDate)}`}
          </div>
        );
      }

      return (
        <div className="text-xs font-semibold">
          Plan Canceled for Non-Payment
        </div>
      );
    }

    if (planEndingSoonData) {
      return (
        <div className="text-xs">
          <span className="font-semibold">
            Plan Ends
          </span>
          {`: ${formatDateString(planEndingSoonData.endDate, user?.location?.timeZone)}`}
        </div>
      );
    }

    if (hasUpcomingRenewal) {
      return (
        <div className="text-xs">
          <span className="font-semibold">
            Plan Ends
          </span>
          {`: ${fromISOToFormatDate(renewalStartDate ?? "-")}`}
        </div>
      );
    }

    return (
      <div className="text-xs">
        <span className="font-semibold">
          Next Billing
        </span>
        {`: ${formatDateString(nextBillingDate, user?.location?.timeZone)}`}
      </div>
    );
  };

  const renderPlanStatusSecondaryLine = () => {
    if (planOverdueStatus === OVERDUE_STATUSES.OVERDUE) {
      return (
        <div className="text-xs text-gray-1 mt-0.5">
          Please login to your ScratchPay account to manage payments.
        </div>
      );
    }

    if (planOverdueStatus === OVERDUE_STATUSES.PAUSED) {
      if (isPausedRenewUnsuccessful) {
        return (
          <div className="text-xs text-gray-1 mt-0.5">
            Please pay overdue invoices to continue booking appointments.
          </div>
        );
      }

      return (
        <div className="text-xs text-gray-1 mt-0.5">
          {isScratchpay ?
            "Login to your ScratchPay account to make payment and resume benefits." :
            "Please pay overdue invoices to resume benefits"}
        </div>
      );
    }

    if (planOverdueStatus === OVERDUE_STATUSES.CANCELLED) {
      return (
        <div className="text-xs text-gray-1 mt-0.5 w-56">
          Please reach out to your hospital team
          for further assistance
        </div>
      );
    }

    if (planEndingSoonData) return null;

    if (hasUpcomingRenewal) return null;

    return (
      <div className="text-xs text-gray-1 mt-0.5">
        <span className="font-semibold">
          Plan Renews
        </span>
        {`: ${formatDateString(planRenewalDate, user?.location?.timeZone)}`}
      </div>
    );
  };


  const handleReviewTotalCarePlans = () => {
    if (planEndingSoonData) {
      startRenewalFunnel(
        [planEndingSoonData],
        WELLNESS_RENEWAL_ENTRY_POINTS.PLAN_AND_BILLING_RENEWAL_BANNER,
      );
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl p-4 flex flex-col gap-4 shadow-lg ">
        {planEndingSoonData && (
          <TotalCareRenewalBanner
            onCTAClick={handleReviewTotalCarePlans}
          />
        )}
        {((planOverdueStatus === OVERDUE_STATUSES.CANCELLED && isPetPlanCancelled) ||
       planOverdueStatus === OVERDUE_STATUSES.PAUSED ||
       planOverdueStatus === OVERDUE_STATUSES.OVERDUE) && planOverdueStatus &&
        <div className={`text-center text-sm
        ${overdueBannerClassName} font-semibold rounded py-0.5`}>
          {getPlanOverdueText(planOverdueStatus, isPausedRenewUnsuccessful,
            isCancelledRenewUnsuccessful, isPlanEndingInFiveDays)}
        </div>}
        <div className="flex gap-3 relative">
          <div>
            <AvatarCustom
              image={activePetDetails?.image ?? ""}
              width={65}
              height={65}
              name={activePetDetails?.name}
              isWellnessMember={planOverdueStatus !== null &&
                (activePetDetails?.isWellnessplan && !isPetPlanCancelled) ||
               planOverdueStatus !== OVERDUE_STATUSES.CANCELLED}
              isPlanOverdue={planOverdueStatus !== null &&
              ((activePetDetails?.isWellnessplan && isPetPlanCancelled) ||
              isCancelledRenewUnsuccessful || isPausedRenewUnsuccessful ||
               planOverdueStatus === OVERDUE_STATUSES.CANCELLED)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-xl font-semibold">{planName ? planName : "GoodPet+ Plan"}&nbsp;
              {renderCardStatusTag()}
            </div>
            <div className="flex flex-col gap-1 text-sm">
              <div>{activePetDetails?.name}</div>
              {isScratchpay &&
                planOverdueStatus !== OVERDUE_STATUSES.PAUSED &&
                planOverdueStatus !== OVERDUE_STATUSES.CANCELLED && (
                <div className="text-violet-1 text-xs w-fit">
                  Monthly Payment via ScratchPay
                </div>
              )}
              {!isScratchpay && (
                planOverdueStatus !== OVERDUE_STATUSES.CANCELLED && !isPausedRenewUnsuccessful &&
                <div className="text-violet-1">{!subscriptionFee? "N/A":`$${subscriptionFee} 
                ${billCycle === "perMonth" ? "/month" : "/year" } `}</div>
              )}
              <div>
                {renderPlanStatusPrimaryLine()}
                {renderPlanStatusSecondaryLine()}
              </div>
            </div>
          </div>
        </div>

        {planOverdueStatus && (planOverdueStatus !== OVERDUE_STATUSES.CANCELLED) && !isScratchpay &&
      <PetInvoiceList petId={activePetDetails?.id} petName={activePetDetails.name} />
        }

        {(planOverdueStatus === OVERDUE_STATUSES.PAUSED ||
          planOverdueStatus === OVERDUE_STATUSES.OVERDUE) &&
          isScratchpay && (
          <div>
            <StyledBookingFlowButton
              disabled={false}
              fullWidth
              variant="contained"
              color="primary"
              size="small"
              onClick={handleScratchPayNowClick}
            >
              {"Pay Now"}
            </StyledBookingFlowButton>
          </div>
        )}

        {!planOverdueStatus && <div
          onClick={() => handleManagePlanCtaClick(activePetDetails.id, activePetDetails.name )}
          className={`border border-gray-1 rounded-[6.25rem] py-2 cursor-pointer
        px-5 flex justify-center text-xs font-semibold hover:bg-gray-3 
        ${!hasWellnessPlan || hasUpcomingRenewal ? "bg-gray-3 border-gray-3 pointer-events-none text-gray-1" : ""}`}>
          {"Manage Plan"}
        </div>}
        {planOverdueStatus && (planOverdueStatus === OVERDUE_STATUSES.CANCELLED) &&
        <div onClick={() => handleContactCtaClick()}
          className="border border-gray-4 rounded-[6.25rem] py-2 cursor-pointer
          px-5 flex justify-center text-xs font-semibold hover:bg-gray-3">
          {"Contact Hospital"}
        </div>}
      </div>
    </>
  );
};

export default PetPlanCard;
